import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  EccezioneNegozio,
  ExtraCalendarUserInterface,
} from '../../interfaces/ExtraCalendarUser';
import { FinestraAssenze } from '../../pages/Calendars/utility_objects/AssenzeOperatoriUtils';

export interface DatiAssenze {
  righe: ExtraCalendarUserInterface[];
  eccezioniNegozio: EccezioneNegozio[];
}

const getRighe = async <T>(
  url: string,
  inizio: string,
  fine: string,
  signal?: AbortSignal
): Promise<T[]> => {
  const {
    data: { data },
  } = await axios.get(url, {
    params: { startTime: inizio, endTime: fine, dc: Date.now() },
    signal,
  });
  return data ?? [];
};

// Righe ExtraCalendarUser (straordinari e assenze) degli operatori del punto vendita
// e eccezioni del negozio che si sovrappongono a [inizio, fine]: il filtro per
// sovrapposizione lo fa il backend. Le eccezioni del negozio servono a riconoscere
// le loro copie sugli utenti. L'errore viene rilanciato (non trasformato in lista
// vuota) perché la fascia non deve dire "nessun assente" quando la chiamata è
// fallita.
export const getAssenzeOperatori = async (
  pointOfSaleId: string,
  inizio: string,
  fine: string,
  signal?: AbortSignal
): Promise<DatiAssenze> => {
  try {
    const [righe, eccezioniNegozio] = await Promise.all([
      getRighe<ExtraCalendarUserInterface>(
        `/api/ExtraCalendarUser/PointOfSale/${pointOfSaleId}`,
        inizio,
        fine,
        signal
      ),
      getRighe<EccezioneNegozio>(
        `/api/ExtraCalendarPointOfSale/PointOfSale/${pointOfSaleId}`,
        inizio,
        fine,
        signal
      ),
    ]);
    return { righe, eccezioniNegozio };
  } catch (error) {
    if (!axios.isCancel(error)) console.trace(error);
    throw error;
  }
};

/**
 * Assenze della fascia Presenze. `finestra` è quella ancorata alla settimana di
 * getFinestraAssenze: Giorno, 3 Giorni, Settimana e Agenda della stessa settimana
 * condividono la chiave, e spostandosi di un giorno non partono altre chiamate.
 */
export const useAssenzeOperatori = ({
  pointOfSaleId,
  finestra,
  enabled,
}: {
  pointOfSaleId: string;
  finestra: FinestraAssenze;
  enabled: boolean;
}) =>
  useQuery({
    queryKey: [
      'assenze-operatori',
      pointOfSaleId,
      finestra.inizio,
      finestra.fine,
    ],
    queryFn: ({ signal }) =>
      getAssenzeOperatori(
        pointOfSaleId,
        finestra.inizio,
        finestra.fine,
        signal
      ),
    enabled: enabled && !!pointOfSaleId,
    // Le app che montano il pacchetto hanno useErrorBoundary: true di default e
    // nessun ErrorBoundary: senza questa opzione un errore della chiamata
    // smonterebbe tutta la pagina invece di mostrare "Assenze non disponibili".
    useErrorBoundary: false,
    // FOCUS WEB tiene i dati un giorno senza ricaricarli al mount: qui invece
    // un'assenza appena inserita nei turni deve comparire tornando in agenda, ma
    // dentro i 60 secondi non si richiede.
    staleTime: 60000,
    // La console di FOCUS 10 ha cacheTime 0 di default e FOCUS WEB un giorno:
    // senza questo valore, nella console i dati sparirebbero appena la fascia o la
    // settimana escono di vista, e i 60 secondi qui sopra varrebbero solo in FOCUS WEB.
    cacheTime: 300000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: 300000,
  });
